// src/pages/AssetMap.tsx — Asset Explorer Map (governed Geospatial surface)
//
// Interactive Leaflet map of the council's Conquest asset estate, served live
// from the dw_gold Fabric Warehouse via /api/asset-map/*. Governed: gates on
// geospatial:asset_map (admins bypass).
//
// Layers are point asset classes (trees, playgrounds, drainage pits, …). Points
// are fetched for the current viewport (bbox), capped server-side, and clustered.
// Clicking a feature loads its full attribute + asset-master detail on demand.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, SpinnerSize, mergeStyleSets, keyframes, MessageBar, MessageBarType, Dropdown, IDropdownOption } from '@fluentui/react';
import { MapContainer, TileLayer, ScaleControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import {
  CaretLeft, ArrowsClockwise, MapPin, X as PhosphorX, Stack, Info,
} from '@phosphor-icons/react';
import { accessService } from '../services/accessService';
import { boroondaraPalette } from '../theme/boroondaraTheme';
import { BASEMAPS, BasemapKey } from '../components/geospatial/constants';

const p = boroondaraPalette;
const TEAL = '#0F766E';
// Centre on the Boroondara LGA (the shared DEFAULT_CENTER sits north in Manningham)
const BOROONDARA_CENTER: [number, number] = [-37.82, 145.06];
const BOROONDARA_ZOOM = 13;

/* Fix Leaflet default marker icon paths for webpack */
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/* Per-layer marker colours (stable) */
const LAYER_COLORS: Record<string, string> = {
  trees: '#2e873d', playgrounds: '#DB2777', drainage_pits: '#0891B2', street_lights: '#CA8A04',
  tmd_devices: '#7C3AED', litter_bins: '#6B7280', bike_racks: '#0D9488', meters: '#B45309',
  sports_lights: '#EA580C', bollards: '#475569', recycle_bins: '#16A34A', park_tables: '#9333EA',
  drinking_fountains: '#0284C7', carparks: '#1D4ED8', bridges: '#78350F', ev_charging: '#059669',
  defects: '#DC2626', actions: '#EA580C',
};
const PALETTE = ['#0F766E', '#1D4ED8', '#DB2777', '#CA8A04', '#7C3AED', '#0891B2', '#EA580C', '#16A34A'];
const layerColor = (code: string, idx: number) => LAYER_COLORS[code] || PALETTE[idx % PALETTE.length];

/* ── Types ── */
interface LayerDef {
  code: string;
  label: string;
  category: string;
  geometry: 'point' | 'line' | 'polygon';
  source?: 'asset' | 'geometry';
  defaultOn: boolean;
  minZoom: number | null;
  featureCount: number | null;
}
interface GeoFeature {
  type: 'Feature';
  id: number;
  geometry: { type: string; coordinates: any };
  properties: { asset_id: number; layer: string };
}
interface FeatureCollection {
  type: 'FeatureCollection';
  features: GeoFeature[];
  meta: { truncated?: boolean; returned?: number; cap?: number };
}
interface FeatureDetail {
  assetId: number;
  layer: string;
  layerLabel: string;
  attributes: Record<string, any>;
  master: Record<string, any> | null;
}
interface WorkOrderRow {
  dateKey: number | null;
  type: string | null;
  status: string | null;
  description: string | null;
  cost: number | null;
  completed: boolean;
}
interface WorksData {
  counts: { workOrders: number; inspections: number; defects: number };
  workOrders: WorkOrderRow[];
}

// YYYYMMDD int → "DD Mon YYYY"
function fmtDateKey(k: number | null): string {
  if (!k || String(k).length !== 8) return '';
  const s = String(k);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const mi = parseInt(s.slice(4, 6), 10) - 1;
  return `${s.slice(6, 8)} ${months[mi] || '?'} ${s.slice(0, 4)}`;
}

const spin = keyframes({ from: { transform: 'rotate(0)' }, to: { transform: 'rotate(360deg)' } });

const styles = mergeStyleSets({
  page: { minHeight: 'calc(100vh - 52px)', background: '#F5F5F5', fontFamily: "'Segoe UI', system-ui, sans-serif" } as any,
  inner: { maxWidth: 1400, margin: '0 auto', padding: 24, '@media (max-width: 768px)': { padding: 12 } } as any,
  backLink: { display: 'inline-flex', alignItems: 'center', gap: 4, color: TEAL, fontWeight: 600, fontSize: 13, background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginBottom: 12 } as any,
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 18 } as any,
  headerLeft: { display: 'flex', gap: 12, alignItems: 'center' },
  iconTile: { width: 36, height: 36, borderRadius: 9, background: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 },
  title: { fontSize: 26, fontWeight: 800, color: p.text, letterSpacing: '-0.01em' },
  subtitle: { fontSize: 13, color: '#747474', marginTop: 2 },
  refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, color: p.text, cursor: 'pointer' } as any,
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18, '@media (max-width: 860px)': { gridTemplateColumns: 'repeat(2, 1fr)' } } as any,
  kpiCard: { background: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 10, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  kpiLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#747474' } as any,
  kpiValue: { fontSize: 32, fontWeight: 800, color: p.text, fontVariantNumeric: 'tabular-nums', marginTop: 4 } as any,
  kpiSub: { fontSize: 12, color: '#747474', marginTop: 2 },
  splitRow: { display: 'flex', gap: 16, alignItems: 'stretch', '@media (max-width: 900px)': { flexDirection: 'column' } } as any,
  panel: { width: 290, flexShrink: 0, background: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 10, padding: 16, maxHeight: 640, overflowY: 'auto', '@media (max-width: 900px)': { width: 'auto' } } as any,
  panelTitle: { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#747474', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 } as any,
  catGroup: { marginBottom: 14 },
  catLabel: { fontSize: 11, fontWeight: 700, color: p.text, marginBottom: 6 },
  layerItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: p.text, selectors: { ':hover': { background: '#F5F7FA' } } } as any,
  swatch: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  countBadge: { marginLeft: 'auto', fontSize: 11, color: '#9CA3AF', fontVariantNumeric: 'tabular-nums' } as any,
  mapWrap: { flex: 1, minWidth: 0, position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid #E1E1E1', minHeight: 640 } as any,
  truncBar: { position: 'absolute', top: 10, left: 10, zIndex: 500, background: 'rgba(255,251,235,0.96)', border: '1px solid #FDE68A', color: '#92400E', fontSize: 12, borderRadius: 6, padding: '6px 10px', maxWidth: 340, display: 'flex', gap: 6, alignItems: 'flex-start' } as any,
  basemapPick: { position: 'absolute', top: 10, right: 10, zIndex: 500, background: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 6, padding: 4 } as any,
  detail: { position: 'absolute', top: 0, right: 0, bottom: 0, width: 320, background: '#FFFFFF', borderLeft: '1px solid #E1E1E1', zIndex: 600, padding: 18, overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,0.08)' } as any,
  detailHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  detailTitle: { fontSize: 15, fontWeight: 700, color: p.text },
  detailSub: { fontSize: 12, color: TEAL, fontWeight: 600 },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2 } as any,
  attrSectionLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9CA3AF', margin: '16px 0 6px' } as any,
  attrRow: { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', borderBottom: '1px solid #F3F4F6', fontSize: 12 },
  attrKey: { color: '#747474', flexShrink: 0 },
  attrVal: { color: p.text, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' } as any,
  countRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, margin: '4px 0 12px' } as any,
  countChip: { background: '#F9FAFB', border: '1px solid #F0F0F0', borderRadius: 8, padding: '8px 6px', textAlign: 'center' } as any,
  countChipVal: { fontSize: 18, fontWeight: 800, color: p.text, fontVariantNumeric: 'tabular-nums' } as any,
  countChipLbl: { fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9CA3AF', marginTop: 1 } as any,
  woItem: { borderBottom: '1px solid #F3F4F6', padding: '7px 0' },
  woTop: { display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' },
  woType: { fontSize: 12, fontWeight: 700, color: p.text },
  woDate: { fontSize: 11, color: '#9CA3AF', flexShrink: 0, fontVariantNumeric: 'tabular-nums' } as any,
  woDesc: { fontSize: 11.5, color: '#5B5B5B', marginTop: 2, lineHeight: '1.4' },
  woMeta: { fontSize: 10.5, color: '#9CA3AF', marginTop: 2, display: 'flex', gap: 8 },
  gate: { padding: 56, textAlign: 'center', color: '#747474', fontSize: 14, lineHeight: '1.6', background: '#FFFFFF', borderRadius: 10, border: '1px solid #EDEDED', maxWidth: 520, margin: '48px auto' } as any,
  gateTitle: { color: p.text, fontWeight: 700, fontSize: 18, marginBottom: 8 },
  gateLink: { color: TEAL, fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none' } as any,
  spinner: { animationName: spin, animationDuration: '0.9s', animationIterationCount: 'infinite', animationTimingFunction: 'linear', display: 'inline-flex' } as any,
});

/* ── Clustered point layer (self-contained; decoupled from public explorer) ── */
function ClusteredAssetLayer({ features, color, onClick }: {
  features: GeoFeature[];
  color: string;
  onClick: (assetId: number) => void;
}) {
  const map = useMap();
  const ref = useRef<any>(null);

  useEffect(() => {
    if (!features.length) return;
    const cluster = (L as any).markerClusterGroup({
      maxClusterRadius: 48,
      iconCreateFunction: (c: any) => L.divIcon({
        html: `<div style="background:${color};color:#fff;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;box-shadow:0 2px 6px rgba(0,0,0,.25);border:2px solid #fff">${c.getChildCount()}</div>`,
        className: '',
        iconSize: L.point(32, 32),
      }),
    });

    features.forEach((f) => {
      if (!f.geometry || f.geometry.type !== 'Point') return;
      const [lng, lat] = f.geometry.coordinates;
      if (typeof lat !== 'number' || typeof lng !== 'number') return;
      const marker = L.circleMarker([lat, lng], { radius: 6, fillColor: color, color: '#fff', weight: 1.5, fillOpacity: 0.9 });
      marker.on('click', () => onClick(f.properties.asset_id));
      cluster.addLayer(marker);
    });

    ref.current = cluster;
    map.addLayer(cluster);
    return () => { if (ref.current) { map.removeLayer(ref.current); ref.current = null; } };
  }, [map, features, color, onClick]);

  return null;
}

/* ── Vector layer (lines & polygons; not clustered) ── */
function VectorAssetLayer({ features, color, geometry, onClick }: {
  features: GeoFeature[];
  color: string;
  geometry: 'line' | 'polygon';
  onClick: (assetId: number) => void;
}) {
  const map = useMap();
  const ref = useRef<any>(null);

  useEffect(() => {
    if (!features.length) return;
    const isPoly = geometry === 'polygon';
    const gj = L.geoJSON(features as any, {
      style: () => ({
        color,
        weight: isPoly ? 1.5 : 2.5,
        opacity: 0.9,
        fillColor: color,
        fillOpacity: isPoly ? 0.25 : 0,
      }),
      onEachFeature: (feat: any, lyr: any) => {
        const id = feat?.properties?.asset_id;
        lyr.on('click', () => id != null && onClick(id));
        lyr.on('mouseover', () => lyr.setStyle && lyr.setStyle({ weight: isPoly ? 2.5 : 4, fillOpacity: isPoly ? 0.4 : 0 }));
        lyr.on('mouseout', () => lyr.setStyle && lyr.setStyle({ weight: isPoly ? 1.5 : 2.5, fillOpacity: isPoly ? 0.25 : 0 }));
      },
    });
    ref.current = gj;
    map.addLayer(gj);
    return () => { if (ref.current) { map.removeLayer(ref.current); ref.current = null; } };
  }, [map, features, color, geometry, onClick]);

  return null;
}

/* ── Watches the map viewport, reports the bbox + zoom (debounced) ── */
function BoundsWatcher({ onBbox }: { onBbox: (bbox: string, zoom: number) => void }) {
  const timer = useRef<any>(null);
  const emit = useCallback((mapObj: L.Map) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const b = mapObj.getBounds();
      onBbox(`${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`, mapObj.getZoom());
    }, 400);
  }, [onBbox]);

  const map = useMapEvents({
    moveend: () => emit(map),
    zoomend: () => emit(map),
  });
  // Emit once on mount
  useEffect(() => { emit(map); }, [map, emit]);
  return null;
}

/* ── Flies the map to a target bounds whenever it changes (focus-on-select) ── */
function FitController({ target }: { target: L.LatLngBoundsExpression | null }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.fitBounds(target, { padding: [50, 50], maxZoom: 17, animate: true });
  }, [map, target]);
  return null;
}

export default function AssetMap() {
  const nav = useNavigate();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  const [layers, setLayers] = useState<LayerDef[]>([]);
  const [active, setActive] = useState<Set<string>>(new Set());
  const [featuresByLayer, setFeaturesByLayer] = useState<Record<string, GeoFeature[]>>({});
  const [truncatedLayers, setTruncatedLayers] = useState<Set<string>>(new Set());
  const [loadingLayers, setLoadingLayers] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<{ code: string; assetId: number } | null>(null);
  const [detail, setDetail] = useState<FeatureDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [works, setWorks] = useState<WorksData | null>(null);
  const [worksLoading, setWorksLoading] = useState(false);
  const [basemap, setBasemap] = useState<BasemapKey>('Street');
  const [bbox, setBbox] = useState<string>('');
  const [zoom, setZoom] = useState<number>(14);
  const [fitTarget, setFitTarget] = useState<L.LatLngBoundsExpression | null>(null);
  const [error, setError] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  // ── Access gate ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; allowedGeospatial?: string[] }>('/me');
        if (!alive) return;
        const d = res.data;
        setHasAccess(d.role === 'admin' ? true : Array.isArray(d.allowedGeospatial) && d.allowedGeospatial.includes('asset_map'));
      } catch {
        if (alive) setHasAccess(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── Load layer catalog ──
  const loadCatalog = useCallback(async () => {
    try {
      const res = await accessService.get<{ layers: LayerDef[]; meta: { generatedAt: string } }>('/asset-map/layers');
      setLayers(res.data.layers);
      setActive((prev) => (prev.size ? prev : new Set(res.data.layers.filter((l) => l.defaultOn).map((l) => l.code))));
      if (res.data.meta?.generatedAt) setUpdatedAt(new Date(res.data.meta.generatedAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }));
    } catch {
      setError('Could not load the layer catalogue from the warehouse.');
    }
  }, []);

  useEffect(() => { if (hasAccess) loadCatalog(); }, [hasAccess, loadCatalog]);

  // ── Fetch features for one layer at the current bbox ──
  const fetchLayer = useCallback(async (code: string, box: string) => {
    setLoadingLayers((s) => new Set(s).add(code));
    try {
      const res = await accessService.get<FeatureCollection>(`/asset-map/layers/${code}/features`, {
        params: box ? { bbox: box } : {},
      });
      setFeaturesByLayer((prev) => ({ ...prev, [code]: res.data.features }));
      setTruncatedLayers((prev) => {
        const next = new Set(prev);
        if (res.data.meta?.truncated) next.add(code); else next.delete(code);
        return next;
      });
    } catch {
      setError(`Could not load features for "${code}".`);
    } finally {
      setLoadingLayers((s) => { const n = new Set(s); n.delete(code); return n; });
    }
  }, []);

  // Layer lookup + zoom-gate helper (a layer only loads at/above its minZoom)
  const layerByCode = React.useMemo(() => new Map(layers.map((l) => [l.code, l])), [layers]);
  const belowMinZoom = useCallback((code: string) => {
    const l = layerByCode.get(code);
    return !!(l && l.minZoom && zoom < l.minZoom);
  }, [layerByCode, zoom]);

  // ── (Re)fetch active layers when the viewport, zoom or active set changes ──
  // Zoom-gated layers below their minZoom are skipped (and their features cleared).
  useEffect(() => {
    if (!hasAccess || !bbox) return;
    active.forEach((code) => {
      if (belowMinZoom(code)) {
        setFeaturesByLayer((prev) => (prev[code] ? { ...prev, [code]: [] } : prev));
      } else {
        fetchLayer(code, bbox);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, bbox, zoom, active, fetchLayer, belowMinZoom]);

  // Drop cached features for layers no longer active
  useEffect(() => {
    setFeaturesByLayer((prev) => {
      const next: Record<string, GeoFeature[]> = {};
      active.forEach((code) => { if (prev[code]) next[code] = prev[code]; });
      return next;
    });
  }, [active]);

  // ── Feature detail on selection ──
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let alive = true;
    setDetailLoading(true);
    (async () => {
      try {
        const res = await accessService.get<FeatureDetail>(`/asset-map/feature/${selected.code}/${selected.assetId}`);
        if (alive) setDetail(res.data);
      } catch {
        if (alive) setDetail(null);
      } finally {
        if (alive) setDetailLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selected]);

  // ── Works & condition history on selection (loads progressively, in parallel) ──
  // Only asset-source layers are asset-keyed; defect/action overlays carry their
  // own detail on the linked fact, so skip the works fetch for those.
  useEffect(() => {
    if (!selected) { setWorks(null); return; }
    const src = layers.find((l) => l.code === selected.code)?.source;
    if (src === 'geometry') { setWorks(null); setWorksLoading(false); return; }
    let alive = true;
    setWorksLoading(true);
    (async () => {
      try {
        const res = await accessService.get<WorksData>(`/asset-map/works/${selected.assetId}`);
        if (alive) setWorks(res.data);
      } catch {
        if (alive) setWorks(null);
      } finally {
        if (alive) setWorksLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selected, layers]);

  // Fetch a layer's overall extent and fly the map to it (focus-on-select).
  const focusLayer = useCallback(async (code: string) => {
    try {
      const res = await accessService.get<{ bounds: number[] | null }>(`/asset-map/layers/${code}/extent`);
      const b = res.data.bounds;
      if (b && b.length === 4) {
        // API returns [minLng, minLat, maxLng, maxLat]; Leaflet wants [[S,W],[N,E]]
        setFitTarget([[b[1], b[0]], [b[3], b[2]]]);
      }
    } catch { /* focus is best-effort */ }
  }, []);

  const toggleLayer = (code: string) => {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
        focusLayer(code); // turning a layer on flies the map to its features
      }
      return next;
    });
  };

  const refresh = async () => {
    setRefreshing(true);
    setFeaturesByLayer({});
    await loadCatalog();
    if (bbox) active.forEach((code) => fetchLayer(code, bbox));
    setRefreshing(false);
  };

  // ── Derived KPIs ──
  const visibleCount = Object.values(featuresByLayer).reduce((a, f) => a + f.length, 0);
  const totalInActive = layers.filter((l) => active.has(l.code)).reduce((a, l) => a + (l.featureCount || 0), 0);
  const grandTotal = layers.reduce((a, l) => a + (l.featureCount || 0), 0);
  const anyTruncated = truncatedLayers.size > 0;

  // Group layers by category for the panel
  const byCategory: Record<string, LayerDef[]> = {};
  layers.forEach((l) => { (byCategory[l.category] = byCategory[l.category] || []).push(l); });

  // Source of the currently-selected feature's layer (asset vs geometry overlay)
  const selectedSource = selected ? layerByCode.get(selected.code)?.source : undefined;

  if (hasAccess === null) {
    return <div className={styles.page}><div style={{ padding: 80, textAlign: 'center' }}><Spinner size={SpinnerSize.large} label="Checking access…" /></div></div>;
  }
  if (hasAccess === false) {
    return (
      <div className={styles.page}>
        <div className={styles.gate}>
          <div className={styles.gateTitle}>Access Required</div>
          <div>The Asset Explorer Map draws on governed enterprise data. Request access to view it, or head back to Geospatial.</div>
          <div style={{ marginTop: 16, display: 'flex', gap: 16, justifyContent: 'center' }}>
            <button className={styles.gateLink} onClick={() => nav('/onboard')}>Request access →</button>
            <button className={styles.gateLink} onClick={() => nav('/geospatial')}>Back to Geospatial</button>
          </div>
        </div>
      </div>
    );
  }

  const basemapOptions: IDropdownOption[] = (Object.keys(BASEMAPS) as BasemapKey[]).map((k) => ({ key: k, text: k }));

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <button className={styles.backLink} onClick={() => nav('/geospatial')}>
          <CaretLeft size={13} weight="bold" /> Geospatial
        </button>

        <div className={styles.headerRow}>
          <div className={styles.headerLeft}>
            <div className={styles.iconTile}><MapPin size={20} weight="fill" /></div>
            <div>
              <div className={styles.title}>Asset Explorer Map</div>
              <div className={styles.subtitle}>Source: Conquest via dw_gold{updatedAt ? ` · Updated ${updatedAt}` : ''}</div>
            </div>
          </div>
          <button className={styles.refreshBtn} onClick={refresh}>
            <span className={refreshing ? styles.spinner : undefined}><ArrowsClockwise size={14} weight="bold" /></span>
            Refresh
          </button>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')} styles={{ root: { borderRadius: 8, marginBottom: 14 } }}>
            {error}
          </MessageBar>
        )}

        {/* KPI row */}
        <div className={styles.kpiRow}>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>Features in view</div>
            <div className={styles.kpiValue}>{visibleCount.toLocaleString('en-AU')}</div>
            <div className={styles.kpiSub}>across active layers</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>In active layers</div>
            <div className={styles.kpiValue}>{totalInActive.toLocaleString('en-AU')}</div>
            <div className={styles.kpiSub}>total mapped assets</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>Layers active</div>
            <div className={styles.kpiValue}>{active.size}</div>
            <div className={styles.kpiSub}>of {layers.length} available</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>Mapped estate</div>
            <div className={styles.kpiValue}>{grandTotal.toLocaleString('en-AU')}</div>
            <div className={styles.kpiSub}>geolocated assets</div>
          </div>
        </div>

        {/* Split: layer panel + map */}
        <div className={styles.splitRow}>
          <div className={styles.panel}>
            <div className={styles.panelTitle}><Stack size={14} weight="bold" /> Layers</div>
            {Object.keys(byCategory).sort().map((cat) => (
              <div key={cat} className={styles.catGroup}>
                <div className={styles.catLabel}>{cat}</div>
                {byCategory[cat].map((l, idx) => (
                  <div key={l.code} className={styles.layerItem} onClick={() => toggleLayer(l.code)}>
                    <input type="checkbox" checked={active.has(l.code)} readOnly style={{ accentColor: TEAL }} />
                    <span className={styles.swatch} style={{ background: layerColor(l.code, idx) }} />
                    <span>{l.label}</span>
                    {active.has(l.code) && belowMinZoom(l.code)
                      ? <span className={styles.countBadge} style={{ color: '#B45309' }}>zoom in</span>
                      : loadingLayers.has(l.code)
                        ? <span className={styles.countBadge}><Spinner size={SpinnerSize.xSmall} /></span>
                        : <span className={styles.countBadge}>{l.featureCount != null ? l.featureCount.toLocaleString('en-AU') : '—'}</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className={styles.mapWrap}>
            {anyTruncated && (
              <div className={styles.truncBar}>
                <Info size={14} weight="bold" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Showing a capped sample for some layers. Zoom in to load all features in view.</span>
              </div>
            )}
            <div className={styles.basemapPick}>
              <Dropdown
                selectedKey={basemap}
                options={basemapOptions}
                onChange={(_, o) => o && setBasemap(o.key as BasemapKey)}
                styles={{ root: { width: 120 }, title: { fontSize: 12, height: 30, lineHeight: '28px' } }}
              />
            </div>

            <MapContainer center={BOROONDARA_CENTER} zoom={BOROONDARA_ZOOM} style={{ height: '100%', width: '100%', minHeight: 640 }} scrollWheelZoom>
              <TileLayer url={BASEMAPS[basemap].url} attribution={BASEMAPS[basemap].attribution} />
              <ScaleControl position="bottomleft" />
              <BoundsWatcher onBbox={(b, z) => { setBbox(b); setZoom(z); }} />
              <FitController target={fitTarget} />
              {Array.from(active).map((code, idx) => {
                const l = layerByCode.get(code);
                const geom = l?.geometry || 'point';
                const feats = belowMinZoom(code) ? [] : (featuresByLayer[code] || []);
                if (geom === 'point') {
                  return (
                    <ClusteredAssetLayer
                      key={code}
                      features={feats}
                      color={layerColor(code, idx)}
                      onClick={(assetId) => setSelected({ code, assetId })}
                    />
                  );
                }
                return (
                  <VectorAssetLayer
                    key={code}
                    features={feats}
                    color={layerColor(code, idx)}
                    geometry={geom as 'line' | 'polygon'}
                    onClick={(assetId) => setSelected({ code, assetId })}
                  />
                );
              })}
            </MapContainer>

            {/* Feature detail slide-in */}
            {selected && (
              <div className={styles.detail}>
                <div className={styles.detailHead}>
                  <div>
                    <div className={styles.detailSub}>{detail?.layerLabel || ''}</div>
                    <div className={styles.detailTitle}>Asset {selected.assetId}</div>
                  </div>
                  <button className={styles.closeBtn} onClick={() => setSelected(null)}><PhosphorX size={18} weight="bold" /></button>
                </div>

                {detailLoading && <Spinner size={SpinnerSize.medium} label="Loading detail…" />}

                {!detailLoading && detail && (
                  <>
                    {detail.master && (
                      <>
                        <div className={styles.attrSectionLabel}>Asset Record</div>
                        {Object.entries(detail.master)
                          .filter(([, v]) => v !== null && String(v).trim() !== '')
                          .map(([k, v]) => (
                            <div key={k} className={styles.attrRow}>
                              <span className={styles.attrKey}>{k}</span>
                              <span className={styles.attrVal}>{String(v)}</span>
                            </div>
                          ))}
                      </>
                    )}
                    <div className={styles.attrSectionLabel}>Layer Attributes</div>
                    {Object.entries(detail.attributes).length === 0 && (
                      <div style={{ fontSize: 12, color: '#9CA3AF' }}>No attributes recorded.</div>
                    )}
                    {Object.entries(detail.attributes).map(([k, v]) => (
                      <div key={k} className={styles.attrRow}>
                        <span className={styles.attrKey}>{k}</span>
                        <span className={styles.attrVal}>{String(v)}</span>
                      </div>
                    ))}
                  </>
                )}

                {!detailLoading && !detail && (
                  <div style={{ fontSize: 13, color: '#9CA3AF' }}>No detail available for this feature.</div>
                )}

                {/* Works & condition (asset-source layers only; loads progressively) */}
                {selectedSource !== 'geometry' && (
                <>
                <div className={styles.attrSectionLabel}>Works &amp; Condition</div>
                {worksLoading && !works && <Spinner size={SpinnerSize.small} label="Loading history…" />}
                {works && (
                  <>
                    <div className={styles.countRow}>
                      <div className={styles.countChip}>
                        <div className={styles.countChipVal}>{works.counts.workOrders.toLocaleString('en-AU')}</div>
                        <div className={styles.countChipLbl}>Work Orders</div>
                      </div>
                      <div className={styles.countChip}>
                        <div className={styles.countChipVal}>{works.counts.inspections.toLocaleString('en-AU')}</div>
                        <div className={styles.countChipLbl}>Inspections</div>
                      </div>
                      <div className={styles.countChip}>
                        <div className={styles.countChipVal}>{works.counts.defects.toLocaleString('en-AU')}</div>
                        <div className={styles.countChipLbl}>Defects</div>
                      </div>
                    </div>
                    {works.workOrders.length > 0 ? (
                      <>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                          Recent work orders
                        </div>
                        {works.workOrders.map((w, i) => (
                          <div key={i} className={styles.woItem}>
                            <div className={styles.woTop}>
                              <span className={styles.woType}>{w.type || 'Work order'}</span>
                              <span className={styles.woDate}>{fmtDateKey(w.dateKey)}</span>
                            </div>
                            {w.description && <div className={styles.woDesc}>{w.description}</div>}
                            <div className={styles.woMeta}>
                              {w.status && <span>{w.status}</span>}
                              {w.completed && <span>· Completed</span>}
                              {w.cost != null && w.cost > 0 && <span>· ${w.cost.toLocaleString('en-AU')}</span>}
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: '#9CA3AF' }}>No work orders recorded for this asset.</div>
                    )}
                  </>
                )}
                {!worksLoading && !works && (
                  <div style={{ fontSize: 12, color: '#9CA3AF' }}>No operational history available.</div>
                )}
                </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
